import * as React from "react";
import { FC, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { InstitutionCodesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { InstitutionSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionActions from "../../../actions/institution";
import * as InstitutionCodeActions from "../../../actions/institutionCode";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./InstitutionCodesPage.scss";

interface InstitutionCodesPageProps {
    institution: Dtos.Institution;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    institutionCodes: Dtos.InstitutionCode[];
    loadingInstitutionCodes: boolean;
    loadInstitutionCodesSuccess: boolean;
    loadInstitutionCodesFailure: boolean;
    loadInstitutionCodesErrorCode: string;
    formFilter: IFormFilter;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    clearInstitutionCodes: InstitutionCodeActions.IInstitutionCodeClearActionCreator;
    loadInstitutionCodes: InstitutionCodeActions.IInstitutionCodeLoadByInstitutionIdActionCreator;
    filter: InstitutionCodeActions.IInstitutionCodeFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InstitutionCodesPage: FC<InstitutionCodesPageProps> = ({
    institution,
    loadingInstitution,
    loadInstitutionSuccess,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    institutionCodes,
    loadingInstitutionCodes,
    loadInstitutionCodesSuccess,
    loadInstitutionCodesFailure,
    loadInstitutionCodesErrorCode,
    formFilter,
    permissions,
    pageSize,
    clearInstitution,
    loadInstitution,
    clearInstitutionCodes,
    loadInstitutionCodes,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showInstitutionCodes = !loadingInstitution && loadInstitutionSuccess && !loadingInstitutionCodes && loadInstitutionCodesSuccess;


    // Load Institution & Codes
    useEffect(() => {
        if (institutionIdNumber && !institution && !loadingInstitution) {
            setDrawerItems(InstitutionSubMenu(institutionIdNumber, permissions));
            setBreadcrumbs(InstitutionCodesBreadcrumbs(institutionIdNumber));

            loadInstitution(institutionIdNumber);
            loadInstitutionCodes(institutionIdNumber);
        }
    }, [
        institutionIdNumber,
        institution,
        loadingInstitution,
        setDrawerItems,
        setBreadcrumbs,
        InstitutionCodesBreadcrumbs,
        loadInstitution,
        loadInstitutionCodes,
        InstitutionSubMenu
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institution) {
            setBreadcrumbs(
                InstitutionCodesBreadcrumbs(
                    institutionIdNumber,
                    institution?.name
                )
            );
        }
    }, [
        institutionIdNumber,
        institution,
        setBreadcrumbs,
        InstitutionCodesBreadcrumbs
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionCodesFailure) {
            navigate(`/error/${loadInstitutionCodesErrorCode}`);
        }
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
    }, [
        loadInstitutionCodesFailure, 
        loadInstitutionCodesErrorCode, 
        loadInstitutionFailure, 
        loadInstitutionErrorCode, 
        navigate
    ]);

    // Cleanup effect to clear the institution & codes on component unmount
    useEffect(() => {
        return () => {
            clearInstitution();
            clearInstitutionCodes();
        };
    }, [clearInstitution, clearInstitutionCodes]);

    const createTable = useMemo(() => {
        if (!institutionCodes || institutionCodes.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no institution codes to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = institutionCodes.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={institutionCodes}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : institutionCodes.length
                    }
                    pageSize={showPagination ? pageSize : institutionCodes.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no labs to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    `/institutions/${institutionIdNumber}/codes/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "id",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.SQUARECODE}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "institutionCoding.value",
                            Header: "Group Name",
                            accessor: "institutionCoding.value",
                        },
                        {
                            id: "codeValue",
                            Header: "Code",
                            accessor: "codeValue",
                        },
                    ]}
                />
            </div>
        );
    }, [institutionCodes, pageSize, PAGE_SIZE_OPTIONS, navigate, setPageSize]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Codes</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                            hideActiveToggle={true}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionCodeCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/institutions/${institutionIdNumber}/codes/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInstitutionCodes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionCodes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        AuthenticationHelper,
        permissions,
        showInstitutionCodes,
        createTable,
        institutionIdNumber
    ]);

    return (
        <RestrictedLayout
            loading={loadingInstitution || loadingInstitutionCodes}
            permission={Dtos.Permission.InstitutionCodeView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<InstitutionCodesPageProps> => {
    let institution: Dtos.Institution | undefined = !(
            state.institutions.formData instanceof Array
        )
            ? state.institutions.formData
            : undefined;
    let institutionCodes: Dtos.InstitutionCode[] | undefined = (
            state.institutionCodes.formFilteredData instanceof Array
        )
            ? state.institutionCodes.formFilteredData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institution: institution!,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,

        institutionCodes: institutionCodes!,
        loadingInstitutionCodes:
            state.institutionCodes.loadState &&
            state.institutionCodes.loadState.status == RequestState.Pending,
        loadInstitutionCodesSuccess:
            state.institutionCodes.loadState &&
            state.institutionCodes.loadState.status == RequestState.Success,
        loadInstitutionCodesFailure:
            state.institutionCodes.loadState &&
            state.institutionCodes.loadState.status == RequestState.Failure,
        loadInstitutionCodesErrorCode: state.institutionCodes.loadState.errorCode,

        formFilter: state.institutionCodes.formFilter,
        
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionCodesPageProps> => {
    return {
        loadInstitution: bindActionCreators(InstitutionActions.LoadInstitutionById, dispatch),
        clearInstitution: bindActionCreators(InstitutionActions.Clear, dispatch),
        loadInstitutionCodes: bindActionCreators(InstitutionCodeActions.LoadInstitutionCodeByInstitutionId, dispatch),
        clearInstitutionCodes: bindActionCreators(InstitutionCodeActions.Clear, dispatch),
        filter: bindActionCreators(InstitutionCodeActions.Filter, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InstitutionCodesPage);
